"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getClusterOptions = void 0;
const helpers_1 = require("@turf/helpers");
const centroid_1 = __importDefault(require("@turf/centroid"));
const mapbox_gl_1 = require("mapbox-gl");
const react_1 = __importDefault(require("react"));
const utils_1 = require("../../../utils");
const geojson_utils_1 = require("../../../utils/geojson/geojson-utils");
const _1 = require(".");
const FabricMapMarkers_1 = require("../FabricMapMarkers");
const shouldUseDefaultProp = (cluster, prop) => typeof cluster !== "object" || !(prop in cluster) || !cluster[prop];
const getClusterOptions = (cluster) => ({
    clusterMaxZoom: shouldUseDefaultProp(cluster, "clusterMaxZoom")
        ? 10
        : cluster.clusterMaxZoom,
    clusterRadius: shouldUseDefaultProp(cluster, "clusterRadius")
        ? 20
        : cluster.clusterRadius,
});
exports.getClusterOptions = getClusterOptions;
const makeCenterPoint = (feature) => {
    // I do not like this coercion, but we don't pass GeometryCollections in
    const center = (0, centroid_1.default)(feature);
    center.properties = feature.properties;
    return center;
};
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {
    return;
};
const FabricMapClusters = react_1.default.memo((props) => {
    const { clusterMaxZoom, clusterPaint, clusterProperties, clusterRadius, clusterTextLayout, clusterTextPaint, idPrefix, layer, layersToHideWhenClustering, map, onClusterClick: customClusterClick, extendDefaultClusterClick, onMarkerClick: customMarkerClick, extendDefaultMarkerClick, onClusterMouseEnter, onClusterMouseLeave, onClusterSwitch, } = props;
    const CLUSTER_SOURCE = `${idPrefix}-fabric-map-cluster-source`;
    const CLUSTER_LAYER = `${idPrefix}-fabric-map-cluster`;
    const CLUSTER_LABEL = `${idPrefix}-fabric-map-cluster-label`;
    const SINGLE_LAYER = `${idPrefix}-fabric-map-single-point`;
    const destroyRef = react_1.default.useRef(noop);
    const zoomRef = react_1.default.useRef(map.getZoom());
    // Default values very much eyeballed
    const clusterOptions = react_1.default.useMemo(() => (0, exports.getClusterOptions)({ clusterMaxZoom, clusterRadius }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clusterMaxZoom, clusterRadius]);
    /**
     * Utilities for toggling between cluster and layer visibility based on zoom level.
     */
    const showClusters = react_1.default.useCallback(() => {
        map.setLayoutProperty(CLUSTER_LAYER, "visibility", "visible");
        map.setLayoutProperty(CLUSTER_LABEL, "visibility", "visible");
        map.setLayoutProperty(SINGLE_LAYER, "visibility", "visible");
        layersToHideWhenClustering.forEach((layer) => {
            map.setLayoutProperty(layer, "visibility", "none");
        });
        if (onClusterSwitch) {
            onClusterSwitch(true, map);
        }
    }, [
        map,
        CLUSTER_LAYER,
        CLUSTER_LABEL,
        SINGLE_LAYER,
        layersToHideWhenClustering,
        onClusterSwitch,
    ]);
    const hideClusters = react_1.default.useCallback(() => {
        map.setLayoutProperty(CLUSTER_LAYER, "visibility", "none");
        map.setLayoutProperty(CLUSTER_LABEL, "visibility", "none");
        map.setLayoutProperty(SINGLE_LAYER, "visibility", "none");
        layersToHideWhenClustering.forEach((layer) => {
            map.setLayoutProperty(layer, "visibility", "visible");
        });
        if (onClusterSwitch) {
            onClusterSwitch(false, map);
        }
    }, [
        map,
        CLUSTER_LAYER,
        CLUSTER_LABEL,
        SINGLE_LAYER,
        layersToHideWhenClustering,
        onClusterSwitch,
    ]);
    const onRender = react_1.default.useCallback(() => {
        const singlePointClusterFeatures = map.queryRenderedFeatures(undefined, {
            layers: [SINGLE_LAYER],
        });
        setSinglePointClusters(singlePointClusterFeatures);
    }, [SINGLE_LAYER, map]);
    const onMarkerClick = react_1.default.useCallback((event, feature) => {
        const defaultHandler = () => {
            if (feature) {
                const { coordinates } = feature.geometry;
                map.easeTo({
                    center: mapbox_gl_1.LngLat.convert(coordinates),
                    duration: 1500,
                    zoom: 12,
                });
            }
        };
        if (customMarkerClick) {
            customMarkerClick(event, feature, map);
            if (extendDefaultMarkerClick) {
                defaultHandler();
            }
        }
        else {
            defaultHandler();
        }
    }, [customMarkerClick, extendDefaultMarkerClick, map]);
    /**
     * When clicking a cluster, zoom to get all the centroids in view
     * Won't always break down to field view on large clusters
     */
    const onClusterClick = react_1.default.useCallback((event) => {
        const features = map.queryRenderedFeatures(event.point, {
            layers: [CLUSTER_LAYER],
        }); // We convert everything into MapboxPoints on render
        const source = map.getSource(CLUSTER_SOURCE);
        /**
         * The default behavior, which may be extended by props.onClusterClick
         */
        const defaultHandler = () => {
            var _a, _b, _c, _d;
            const clusterId = (_b = (_a = features[0]) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.cluster_id;
            const pointCount = (_d = (_c = features[0]) === null || _c === void 0 ? void 0 : _c.properties) === null || _d === void 0 ? void 0 : _d.point_count;
            if (source.type === "geojson") {
                source.getClusterLeaves(clusterId, pointCount, 0, (err, clusterLeaves) => {
                    if (err) {
                        return;
                    }
                    const bounds = (0, geojson_utils_1.getBounds)((0, geojson_utils_1.featuresToFeatureCollection)(clusterLeaves));
                    map.fitBounds(bounds, { padding: 250 });
                });
            }
        };
        if (customClusterClick) {
            customClusterClick(event, features[0], map, source);
            if (extendDefaultClusterClick) {
                defaultHandler();
            }
        }
        else {
            defaultHandler();
        }
    }, [
        CLUSTER_LAYER,
        CLUSTER_SOURCE,
        customClusterClick,
        extendDefaultClusterClick,
        map,
    ]);
    /**
     * onZoom spams events, but we track the previous setting
     * so only update visibility when we cross the threshold.
     */
    const onZoom = react_1.default.useCallback(() => {
        const currentZoom = map.getZoom();
        const previousZoom = zoomRef.current;
        if (currentZoom <= clusterOptions.clusterMaxZoom &&
            previousZoom > clusterOptions.clusterMaxZoom) {
            showClusters();
        }
        else if (currentZoom > clusterOptions.clusterMaxZoom &&
            previousZoom <= clusterOptions.clusterMaxZoom) {
            hideClusters();
        }
        zoomRef.current = currentZoom;
    }, [clusterOptions.clusterMaxZoom, hideClusters, map, showClusters]);
    const [singlePointClusters, setSinglePointClusters] = react_1.default.useState([]);
    const singleClusterMarkers = react_1.default.useMemo(() => {
        return (0, _1.makeMarkersFromFeaturesArray)(singlePointClusters, map, onMarkerClick);
    }, [map, onMarkerClick, singlePointClusters]);
    const hoveredCluster = react_1.default.useRef();
    /**
     * This function exists to manage mouseEnter events for clusters.
     * It also handles mouseLeave if the user goes directly from one layer to another.
     */
    const onMouseMove = react_1.default.useCallback((event) => {
        var _a, _b, _c, _d;
        const feature = (_a = event === null || event === void 0 ? void 0 : event.features) === null || _a === void 0 ? void 0 : _a[0];
        const source = map.getSource(CLUSTER_SOURCE);
        if (hoveredCluster.current &&
            ((_b = hoveredCluster.current) === null || _b === void 0 ? void 0 : _b.id) &&
            ((_c = hoveredCluster.current) === null || _c === void 0 ? void 0 : _c.id) !== (feature === null || feature === void 0 ? void 0 : feature.id)) {
            if (onClusterMouseLeave) {
                onClusterMouseLeave(event, hoveredCluster.current, map, source);
            }
            hoveredCluster.current = undefined;
        }
        if (feature && ((_d = hoveredCluster.current) === null || _d === void 0 ? void 0 : _d.id) !== feature.id) {
            hoveredCluster.current = feature;
            if (onClusterMouseEnter) {
                onClusterMouseEnter(event, feature, map, source);
            }
        }
    }, [CLUSTER_SOURCE, map, onClusterMouseEnter, onClusterMouseLeave]);
    /**
     * This function calls the onClusterMouseLeave function when a user mouses from a cluster to the map.
     */
    const onMouseLeave = react_1.default.useCallback((event) => {
        const source = map.getSource(CLUSTER_SOURCE);
        if (hoveredCluster.current) {
            if (onClusterMouseLeave) {
                onClusterMouseLeave(event, hoveredCluster.current, map, source);
            }
            hoveredCluster.current = undefined;
        }
    }, [CLUSTER_SOURCE, map, onClusterMouseLeave]);
    /** Rendering the layer */
    react_1.default.useMemo(() => {
        destroyRef.current();
        utils_1.featureIdGenerator.updateFeatureIds(layer);
        const centerPoints = layer.type === "FeatureCollection"
            ? layer.features.map((feature) => makeCenterPoint(feature))
            : [makeCenterPoint(layer)];
        const centerPointFeatureCollection = (0, helpers_1.featureCollection)(centerPoints);
        /**
         * There are mysterious circumstances under which the destroyRef doesn't
         * work, so if the source is still here, obliterate everything and start over.
         */
        if (map.getSource(CLUSTER_SOURCE)) {
            map.removeLayer(CLUSTER_LAYER);
            map.removeLayer(CLUSTER_LABEL);
            map.removeLayer(SINGLE_LAYER);
            map.removeSource(CLUSTER_SOURCE);
        }
        map.addSource(CLUSTER_SOURCE, Object.assign({ type: "geojson", data: centerPointFeatureCollection, cluster: true, clusterProperties }, clusterOptions));
        map.addLayer({
            id: CLUSTER_LAYER,
            type: "circle",
            filter: ["has", "point_count"],
            source: CLUSTER_SOURCE,
            layout: {
                visibility: map.getZoom() <= clusterOptions.clusterMaxZoom ? "visible" : "none",
            },
            paint: Object.assign({ "circle-color": "rgba(255, 255, 255, 0.8)", "circle-stroke-color": "rgba(255, 255, 255, 0.5)", "circle-stroke-width": 8, "circle-radius": 20 }, clusterPaint),
        });
        map.addLayer({
            id: CLUSTER_LABEL,
            source: CLUSTER_SOURCE,
            type: "symbol",
            filter: ["has", "point_count"],
            layout: Object.assign({ visibility: map.getZoom() <= clusterOptions.clusterMaxZoom ? "visible" : "none", "text-field": "{point_count_abbreviated}", "text-font": ["Avenir LT Pro Bold", "Arial Unicode MS Regular"], "text-offset": [0, 0.3], "text-size": 16 }, clusterTextLayout),
            paint: Object.assign({ "text-color": "#2d64b3" }, clusterTextPaint),
        });
        /**
         * SINGLE_LAYER catches all clusters that have only one point.
         * It renders them as very tiny periods so that we can then query that layer and add markers.
         * Note that there _must_ be content in the layer to actually make it render, and thus be turned into markers.
         */
        map.addLayer({
            id: SINGLE_LAYER,
            type: "symbol",
            source: CLUSTER_SOURCE,
            filter: ["!", ["has", "point_count"]],
            layout: {
                visibility: map.getZoom() <= clusterOptions.clusterMaxZoom ? "visible" : "none",
                "text-field": ".",
                "text-size": 0.001,
            },
        });
        /**
         * Once the map has rendered, take all single points and create markers for them.
         */
        map.on("render", onRender);
        if (map.getZoom() <= clusterOptions.clusterMaxZoom &&
            map.getZoom() === zoomRef.current) {
            showClusters();
        }
        /**
         * Switch between showing the feature collection and showing the clusters
         */
        map.on("zoom", onZoom);
        /**
         * Catch clicks on clusters. Clicks on markers are handled by the markers themselves. The handler is onMarkerClick
         */
        map.on("click", CLUSTER_LAYER, onClusterClick);
        /**
         * Catch `onClusterMouseEnter` events on clusters. `onClusterMouseLeave` events fire here if the user goes straight to another cluster. If they go to the map, they're caught by `onMouseLeave`.
         */
        map.on("mousemove", CLUSTER_LAYER, onMouseMove);
        /**
         * Catch mouseLeave events on clusters if the user mouses from a cluster to the map.
         */
        map.on("mouseleave", CLUSTER_LAYER, onMouseLeave);
        destroyRef.current = () => {
            utils_1.featureIdGenerator.resetFeatureIds(layer);
            if (map && map.getStyle()) {
                if (map.getLayer(CLUSTER_LAYER)) {
                    map.removeLayer(CLUSTER_LAYER);
                }
                if (map.getLayer(CLUSTER_LABEL)) {
                    map.removeLayer(CLUSTER_LABEL);
                }
                if (map.getLayer(SINGLE_LAYER)) {
                    map.removeLayer(SINGLE_LAYER);
                }
                if (map.getSource(CLUSTER_SOURCE)) {
                    map.removeSource(CLUSTER_SOURCE);
                }
                setSinglePointClusters([]);
            }
        };
    }, [
        layer,
        map,
        CLUSTER_SOURCE,
        clusterProperties,
        clusterOptions,
        CLUSTER_LAYER,
        clusterPaint,
        CLUSTER_LABEL,
        clusterTextLayout,
        clusterTextPaint,
        SINGLE_LAYER,
        onRender,
        onZoom,
        onClusterClick,
        onMouseMove,
        onMouseLeave,
        showClusters,
    ]);
    react_1.default.useEffect(() => () => destroyRef.current(), []);
    react_1.default.useLayoutEffect(() => {
        return () => {
            map.off("zoom", onZoom);
            map.off("click", CLUSTER_LAYER, onClusterClick);
            map.off("mousemove", CLUSTER_LAYER, onMouseMove);
            map.off("render", onRender);
        };
    }, [
        idPrefix,
        map,
        onClusterClick,
        CLUSTER_LAYER,
        onRender,
        onZoom,
        onMouseMove,
    ]);
    return react_1.default.useMemo(() => (react_1.default.createElement(react_1.default.Fragment, null, singleClusterMarkers.length && (react_1.default.createElement(FabricMapMarkers_1.FabricMapMarkers, { markers: singleClusterMarkers })))), [singleClusterMarkers]);
});
FabricMapClusters.displayName = "FabricMapClusters";
exports.default = FabricMapClusters;
